// BUILD-046 Owner-requested layout revision (visual edits ledger, Layout-2 and Layout-6): the ONE dropdown button of the page.
//
// Used by the toolbar (Status, Sort), the Filter popover, and the preview strip (Viewing as, Pilot tools). Every one is a REAL <button> with
// aria-haspopup and aria-expanded, a visible focus ring and these keys (the menu-button pattern, as RowMoreMenu):
//   Enter / Space / ArrowDown / ArrowUp open it and put focus on the chosen item (or the first / last); ArrowUp / ArrowDown / Home / End move between the
//   items; Enter or Space on an item chooses it (the menu closes and focus returns to the button); Escape closes and returns focus to the button; Tab leaves
//   and closes; a click outside closes.
// A menu holds single-choice items (`menuitemradio`, aria-checked) and, where a setting is independent of the choices, checkbox items
// (`menuitemcheckbox`, aria-checked), optionally set apart by a separator. A popover holds ordinary form controls (radios, a select) in a labelled group and closes on
// Escape or a click outside. Nothing here knows what a choice does: the host passes `onSelect`.
import { Fragment, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { clampShift } from './popoverClamp';

export const CHEVRON = (
  <svg aria-hidden="true" viewBox="0 0 20 20" className="h-3.5 w-3.5 flex-shrink-0 text-gray-500" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 7.5l5 5 5-5" />
  </svg>
);

export const FUNNEL_ICON = (
  <svg aria-hidden="true" viewBox="0 0 20 20" className="h-4 w-4 flex-shrink-0 text-gray-500" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 4.5h14l-5.5 6.5v4.5l-3 1.5V11z" />
  </svg>
);

/** Closes `open` on a click outside `wrapRef` (and on focus leaving it by Tab). Returns nothing; the owner handles Escape on its own elements. */
export function useOutsideClose(open: boolean, wrapRef: { current: HTMLElement | null }, close: () => void): void {
  useEffect(() => {
    if (!open) return undefined;
    function onPointerDown(event: MouseEvent): void {
      const wrap = wrapRef.current;
      if (wrap !== null && event.target instanceof Node && !wrap.contains(event.target)) close();
    }
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open, wrapRef, close]);
}

const BUTTON_BASE =
  'inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2a7fb8] disabled:opacity-60';

export interface DropdownItem {
  key: string;
  label: string;
  /** Quiet text after the label (for example a count). */
  note?: string;
  selected?: boolean;
  /**
   * 'radio' (default): one choice of a set (`menuitemradio`). 'checkbox': an independent on/off setting (`menuitemcheckbox`); `selected` is its
   * checked state. Either way `aria-checked` carries the state and Enter / Space operate it.
   */
  kind?: 'radio' | 'checkbox';
  /** Draws a thin separator above this item (it separates the checkbox from the choices above it). */
  separatorBefore?: boolean;
  onSelect: () => void;
}

export interface DropdownMenuProps {
  /** The text on the button (it already carries the current choice, for example "Status: Active"). */
  buttonText: ReactNode;
  /** The button's accessible name (it names the control and the current choice). */
  ariaLabel: string;
  /** The menu's accessible name. */
  menuLabel: string;
  items: readonly DropdownItem[];
  icon?: ReactNode;
  align?: 'left' | 'right';
  testId?: string;
  /** Replaces the default outlined look (the preview strip uses its own). */
  buttonClassName?: string;
  disabled?: boolean;
  title?: string;
  /** A quiet line at the top of the open menu (the Sort button shows "Priority +1"; the menu says the whole order). It names the menu through aria-describedby. */
  menuNote?: string | null;
}

export function DropdownMenu({ buttonText, ariaLabel, menuLabel, items, icon, align = 'left', testId, buttonClassName, disabled = false, title, menuNote = null }: DropdownMenuProps) {
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const noteId = useId();
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const focusOnOpen = useRef<'selected' | 'first' | 'last' | null>(null);

  useOutsideClose(open, wrapRef, () => setOpen(false));

  // Opening by keyboard puts focus on the chosen item (or the first / last).
  useEffect(() => {
    if (!open || focusOnOpen.current === null) return;
    const enabled = itemRefs.current.filter((el): el is HTMLButtonElement => el !== null);
    const selectedAt = items.findIndex((item) => item.selected === true);
    const target = focusOnOpen.current === 'last' ? enabled[enabled.length - 1] : focusOnOpen.current === 'selected' && selectedAt >= 0 ? itemRefs.current[selectedAt] : enabled[0];
    focusOnOpen.current = null;
    target?.focus();
  }, [open, items]);

  function close(returnFocus: boolean): void {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  }

  function moveFocus(delta: 1 | -1 | 'first' | 'last'): void {
    const enabled = itemRefs.current.filter((el): el is HTMLButtonElement => el !== null);
    if (enabled.length === 0) return;
    const at = enabled.findIndex((el) => el === document.activeElement);
    let next = 0;
    if (delta === 'first') next = 0;
    else if (delta === 'last') next = enabled.length - 1;
    else next = at < 0 ? (delta === 1 ? 0 : enabled.length - 1) : (at + delta + enabled.length) % enabled.length;
    enabled[next]?.focus();
  }

  function onTriggerKeyDown(event: KeyboardEvent<HTMLButtonElement>): void {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (open) {
        moveFocus(event.key === 'ArrowUp' ? 'last' : 'first');
        return;
      }
      focusOnOpen.current = event.key === 'ArrowUp' ? 'last' : 'selected';
      setOpen(true);
    } else if (event.key === 'Escape' && open) {
      event.preventDefault();
      close(true);
    }
  }

  function onMenuKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        close(true);
        break;
      case 'ArrowDown':
        event.preventDefault();
        moveFocus(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        moveFocus(-1);
        break;
      case 'Home':
        event.preventDefault();
        moveFocus('first');
        break;
      case 'End':
        event.preventDefault();
        moveFocus('last');
        break;
      default:
        break;
    }
  }

  return (
    <div
      ref={wrapRef}
      className="relative inline-block"
      onBlur={(event) => {
        const next = event.relatedTarget;
        if (open && next instanceof Node && wrapRef.current !== null && !wrapRef.current.contains(next)) setOpen(false);
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        data-testid={testId}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={ariaLabel}
        title={title}
        onClick={(event) => {
          focusOnOpen.current = !open && event.detail === 0 ? 'selected' : null;
          setOpen((value) => !value);
        }}
        onKeyDown={onTriggerKeyDown}
        className={buttonClassName ?? BUTTON_BASE}
      >
        {icon}
        <span className="whitespace-nowrap">{buttonText}</span>
        {CHEVRON}
      </button>
      {open && (
        <div
          id={menuId}
          role="menu"
          aria-label={menuLabel}
          aria-describedby={menuNote !== null ? noteId : undefined}
          onKeyDown={onMenuKeyDown}
          className={`absolute z-30 mt-1 min-w-[11rem] max-w-[18rem] rounded-md border border-gray-200 bg-white py-1 shadow-lg ${align === 'right' ? 'right-0' : 'left-0'}`}
        >
          {menuNote !== null && (
            <div id={noteId} data-testid="dropdown-menu-note" className="border-b border-gray-200 px-3 py-1.5 text-xs text-gray-600">
              {menuNote}
            </div>
          )}
          {items.map((item, index) => (
            <Fragment key={item.key}>
              {item.separatorBefore === true && <div role="separator" className="my-1 border-t border-gray-200" />}
              <button
                ref={(el) => {
                  itemRefs.current[index] = el;
                }}
                type="button"
                role={item.kind === 'checkbox' ? 'menuitemcheckbox' : 'menuitemradio'}
                aria-checked={item.selected === true}
                onClick={() => {
                  setOpen(false);
                  triggerRef.current?.focus();
                  item.onSelect();
                }}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-gray-800 hover:bg-gray-50 focus:outline-none focus-visible:bg-sky-50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#2a7fb8]"
              >
                <span aria-hidden="true" className="inline-block w-3 text-xs text-[#2a7fb8]">
                  {item.selected === true ? '\u2713' : ''}
                </span>
                <span className="flex-1">{item.label}</span>
                {item.note !== undefined && <span className="text-xs text-gray-600">{item.note}</span>}
              </button>
            </Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

export interface PopoverButtonProps {
  buttonText: ReactNode;
  ariaLabel: string;
  /** The panel's accessible name. */
  panelLabel: string;
  icon?: ReactNode;
  /** A small mark on the button when something inside is active (the host also puts it in `ariaLabel`, so it is never colour alone). */
  active?: boolean;
  align?: 'left' | 'right';
  testId?: string;
  buttonClassName?: string;
  panelClassName?: string;
  /** Icon only: no label and no chevron (the button keeps its accessible name and its panel). */
  iconOnly?: boolean;
  /** Keeps the panel mounted while closed (hidden), so a confirmation inside it is not lost. */
  keepMounted?: boolean;
  /** Moves the open panel sideways, if it must, so it stays inside the window (the Presets panel is wide). Off by default: the other popovers are unchanged. */
  clampToViewport?: boolean;
  children: ReactNode;
}

/** A button that opens a panel of ordinary controls (Filter, Pilot tools). Escape and a click outside close it; focus returns to the button. */
export function PopoverButton({ buttonText, ariaLabel, panelLabel, icon, active = false, align = 'left', testId, buttonClassName, panelClassName, iconOnly = false, keepMounted = false, clampToViewport = false, children }: PopoverButtonProps) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  useOutsideClose(open, wrapRef, () => setOpen(false));
  // The panel hangs from its button; where that would put it past the window edge it is moved sideways to fit (measured from its natural place, again when the window is resized).
  useLayoutEffect(() => {
    if (!clampToViewport || !open) return undefined;
    const place = (): void => {
      const panel = panelRef.current;
      if (panel === null) return;
      panel.style.transform = 'none';
      const rect = panel.getBoundingClientRect();
      const shift = clampShift(rect.left, rect.right, window.innerWidth);
      panel.style.transform = shift === 0 ? '' : `translateX(${shift}px)`;
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [clampToViewport, open]);
  return (
    <div
      ref={wrapRef}
      className="relative inline-block"
      onKeyDown={(event) => {
        if (event.key === 'Escape' && open) {
          event.preventDefault();
          event.stopPropagation();
          setOpen(false);
          triggerRef.current?.focus();
        }
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        data-testid={testId}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={keepMounted || open ? panelId : undefined}
        aria-label={ariaLabel}
        onClick={() => setOpen((value) => !value)}
        className={buttonClassName ?? BUTTON_BASE}
      >
        {icon}
        {!iconOnly && <span className="whitespace-nowrap">{buttonText}</span>}
        {active && <span aria-hidden="true" className="h-2 w-2 rounded-full bg-[#2a7fb8]" />}
        {!iconOnly && CHEVRON}
      </button>
      {(open || keepMounted) && (
        <div
          ref={panelRef}
          id={panelId}
          role="dialog"
          aria-modal="false"
          aria-label={panelLabel}
          hidden={!open}
          className={`${open ? 'block' : 'hidden'} absolute z-30 mt-1 rounded-md border border-gray-200 bg-white p-3 text-sm shadow-lg ${align === 'right' ? 'right-0' : 'left-0'} ${panelClassName ?? 'w-72 max-w-[calc(100vw-2rem)]'}`}
        >
          {children}
        </div>
      )}
    </div>
  );
}
